import React, { useState } from 'react';
import { TextField, InputAdornment } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CustomButton from '../../../components/buttons/CustomButton';
import PopupBase from '../../../popup/base/PopupBase';
import { OWNER_EXCLUSIVE } from '../../../constants/userRoles';
import personInitials from './personInitials';
import { roleLabel } from './roleVisuals';
import formStyles from './UserFormPopup.module.css';
import styles from './TransferOwnershipPopup.module.css';

/**
 * O(A) Proprietário(a) passa a conta para outra pessoa ativa da equipe.
 * Quem transfere vira Administrador(a); a senha confirma que é a própria pessoa.
 */
function TransferOwnershipPopup({ candidates, saving, error, onClose, onConfirm }) {
  const [targetId, setTargetId] = useState(candidates.length === 1 ? candidates[0].id : null);
  const [password, setPassword] = useState('');
  const target = candidates.find((c) => c.id === targetId);
  const canSubmit = !!target && password.length > 0 && !saving;

  const handleSubmit = () => {
    if (canSubmit) onConfirm(target, password);
  };

  const content = candidates.length === 0 ? (
    <div className={formStyles.content}>
      <p className={styles.empty}>
        A conta só pode ir para alguém que já entra na Gabriela. Convide a pessoa para a equipe
        e, quando ela aceitar o convite, volte aqui.
      </p>
    </div>
  ) : (
    <div className={formStyles.content}>
      <div className={formStyles.field}>
        <span className={formStyles.label}>Quem passa a ser Proprietário(a)</span>
        <div className={styles.people} role="radiogroup" aria-label="Pessoa da equipe">
          {candidates.map((person) => {
            const selected = person.id === targetId;
            return (
              <button
                key={person.id}
                type="button"
                role="radio"
                aria-checked={selected}
                className={`${styles.personOption} ${selected ? styles.selected : ''}`}
                onClick={() => setTargetId(person.id)}
              >
                <span className={formStyles.avatar}>{personInitials(person.name || person.email)}</span>
                <span className={formStyles.personText}>
                  <span className={formStyles.personName}>{person.name || 'Sem nome'}</span>
                  <span className={formStyles.personEmail}>{person.email}</span>
                </span>
                <span className={styles.personRole}>{roleLabel(person.role)}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.changes}>
        <span className={styles.changesTitle}>O que muda</span>
        <ul>
          <li>
            <ArrowForwardRoundedIcon className={styles.bullet} />
            <span>
              <strong>{target ? (target.name || target.email) : 'A pessoa escolhida'}</strong> passa a ter
              acesso a tudo e a confirmar os termos de uso em nome próprio.
            </span>
          </li>
          <li>
            <ArrowForwardRoundedIcon className={styles.bullet} />
            <span>
              <strong>Você</strong> continua na equipe como Administrador(a) e deixa de poder:{' '}
              {OWNER_EXCLUSIVE.map((item) => item.charAt(0).toLowerCase() + item.slice(1)).join('; ')}.
            </span>
          </li>
          <li>
            <ArrowForwardRoundedIcon className={styles.bullet} />
            <span>Assinatura, pacientes e agenda continuam iguais. As duas pessoas recebem um e-mail.</span>
          </li>
        </ul>
      </div>

      <div className={formStyles.field}>
        <label className={formStyles.label} htmlFor="transfer-password">Sua senha</label>
        <TextField
          id="transfer-password"
          type="password"
          fullWidth
          size="small"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
          helperText="Para confirmar que é você. Só quem for Proprietário(a) pode desfazer."
          className={formStyles.input}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon fontSize="small" sx={{ color: 'var(--color-text-light)' }} />
              </InputAdornment>
            ),
          }}
        />
      </div>

      {error && <div className={formStyles.error}>{error}</div>}
    </div>
  );

  const footer = (
    <div className={formStyles.footer}>
      <CustomButton label={candidates.length === 0 ? 'Fechar' : 'Cancelar'} variant="text" onClick={onClose} />
      {candidates.length > 0 && (
        <CustomButton
          label="Transferir a conta"
          variant="contained"
          style="main"
          onClick={handleSubmit}
          loading={saving}
          enabled={canSubmit}
        />
      )}
    </div>
  );

  return (
    <PopupBase
      Title="Transferir a conta"
      Content={content}
      Footer={footer}
      OnClose={onClose}
      maxWidth="sm"
    />
  );
}

export default TransferOwnershipPopup;
